import { useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Button, HottrLockup, Icon } from '@/kit';
import { FOCUS_AREAS } from '@/domains/focusAreas';
import { breakoutsForFocus } from '@/domains/breakouts/catalog';
import { routeSafety } from '@/domains/safety/routing';
import type { FocusAreaId, GoalScope, InterventionStyle } from '@/domains/types';
import { routes } from '@/config/routes';
import { useMeStore } from '@/store/meStore';
import { cn } from '@/kit';

const SCOPES: { id: GoalScope; label: string }[] = [
  { id: 'reduce', label: 'Cut it back' },
  { id: 'stop', label: 'Stop for a while' },
  { id: 'avoid_triggers', label: 'Avoid specific triggers' },
  { id: 'replace', label: 'Replace the loop' },
  { id: 'other', label: 'Something else' },
];

const STYLES: { id: InterventionStyle; label: string; blurb: string }[] = [
  { id: 'direct', label: 'Direct', blurb: 'Short. No soft landings.' },
  { id: 'practical', label: 'Practical', blurb: 'One clear action.' },
  { id: 'gentle', label: 'Steady', blurb: 'Firm, not harsh.' },
];

type Step = 1 | 2 | 3 | 4 | 5;

export function OnboardingPage() {
  const navigate = useNavigate();
  const complete = useMeStore((s) => s.profile?.onboardingComplete);
  const completeOnboarding = useMeStore((s) => s.completeOnboarding);
  const addGoal = useMeStore((s) => s.addGoal);
  const enrolInBreakout = useMeStore((s) => s.enrolInBreakout);

  const [step, setStep] = useState<Step>(1);
  const [focusAreaIds, setFocusAreaIds] = useState<FocusAreaId[]>([]);
  const [stuckSummary, setStuckSummary] = useState('');
  const [differentSummary, setDifferentSummary] = useState('');
  const [helpStyle, setHelpStyle] = useState<InterventionStyle>('practical');
  const [goalStatement, setGoalStatement] = useState(
    'I keep opening hookup apps without deciding to',
  );
  const [desiredChange, setDesiredChange] = useState(
    'I want the app to stop running my evenings',
  );
  const [scope, setScope] = useState<GoalScope>('reduce');
  const [error, setError] = useState<string | null>(null);

  const safety = useMemo(() => routeSafety(focusAreaIds), [focusAreaIds]);
  const appsSelected = focusAreaIds.includes('apps');
  const readyBreakouts = breakoutsForFocus('apps');

  if (complete) return <Navigate to={routes.today} replace />;

  const toggleFocus = (id: FocusAreaId) => {
    setFocusAreaIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const finish = () => {
    setError(null);
    if (!appsSelected) {
      setError('V1 starts with Apps — select Apps to run Get Off the Grid.');
      return;
    }
    if (!stuckSummary.trim() || !differentSummary.trim()) {
      setError('Answer what feels stuck and what different looks like.');
      return;
    }

    completeOnboarding({
      focusAreaIds,
      stuckSummary,
      differentSummary,
      helpStyle,
    });

    const goal = addGoal({
      focusAreaId: 'apps',
      statement: goalStatement,
      desiredChange,
      scope,
    });

    const breakout = readyBreakouts[0];
    if (breakout) {
      enrolInBreakout(breakout.id, goal.id);
    }

    navigate(routes.today);
  };

  return (
    <div className="min-h-screen bg-hottr-ink px-4 py-8 text-white md:px-6">
      <div className="mx-auto max-w-xl">
        <HottrLockup product="me" size="sm" showIcon={false} />

        <p className="mt-8 text-[10px] font-extrabold uppercase tracking-hottr-stamp text-hottr-pink">
          Step {step} of 5
        </p>

        {step === 1 && (
          <section className="mt-3">
            <h1 className="font-display text-3xl font-black uppercase tracking-hottr-display">
              What feels stuck
              <span className="text-hottr-pink">?</span>
            </h1>
            <p className="mt-2 text-sm text-white/55">
              Pick what you want to work on. Multiple is fine. No diagnosis.
            </p>
            <ul className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {FOCUS_AREAS.map((area) => {
                const on = focusAreaIds.includes(area.id);
                return (
                  <li key={area.id}>
                    <button
                      type="button"
                      onClick={() => toggleFocus(area.id)}
                      className={cn(
                        'w-full rounded-sm border px-4 py-4 text-left transition-colors duration-base ease-mechanical',
                        on
                          ? 'border-hottr-pink bg-hottr-pink/10 shadow-neon-soft'
                          : 'border-white/[0.12] bg-white/[0.03] hover:border-white/24',
                      )}
                    >
                      <span className="block text-[12px] font-extrabold uppercase tracking-hottr-label">
                        {area.label}
                      </span>
                      <span className="mt-1 block text-[12px] text-white/55">{area.blurb}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {safety.kind === 'substance' && (
              <p className="mt-4 border border-white/12 bg-white/[0.03] p-3 text-[12px] text-white/72">
                {safety.disclaimer}
              </p>
            )}
            <div className="mt-8">
              <Button
                variant="primary"
                fullWidth
                disabled={focusAreaIds.length === 0}
                onClick={() => setStep(2)}
              >
                Continue
              </Button>
            </div>
          </section>
        )}

        {step === 2 && (
          <section className="mt-3">
            <h1 className="font-display text-3xl font-black uppercase tracking-hottr-display">
              In your words
              <span className="text-hottr-pink">.</span>
            </h1>
            <label className="mt-6 block text-[10px] font-extrabold uppercase tracking-hottr-label text-white/48">
              What feels stuck?
              <textarea
                value={stuckSummary}
                onChange={(e) => setStuckSummary(e.target.value)}
                rows={3}
                className="mt-2 w-full rounded-sm border border-white/12 bg-hottr-charcoal px-3 py-3 text-[14px] text-white outline-none focus:border-hottr-pink"
                placeholder="e.g. I open Grindr every time I’m bored or lonely"
              />
            </label>
            <label className="mt-4 block text-[10px] font-extrabold uppercase tracking-hottr-label text-white/48">
              What would different look like?
              <textarea
                value={differentSummary}
                onChange={(e) => setDifferentSummary(e.target.value)}
                rows={3}
                className="mt-2 w-full rounded-sm border border-white/12 bg-hottr-charcoal px-3 py-3 text-[14px] text-white outline-none focus:border-hottr-pink"
                placeholder="e.g. Evenings that are mine again"
              />
            </label>
            <div className="mt-8 flex gap-2">
              <Button variant="secondary" fullWidth onClick={() => setStep(1)}>
                Back
              </Button>
              <Button
                variant="primary"
                fullWidth
                disabled={!stuckSummary.trim() || !differentSummary.trim()}
                onClick={() => setStep(3)}
              >
                Continue
              </Button>
            </div>
          </section>
        )}

        {step === 3 && (
          <section className="mt-3">
            <h1 className="font-display text-3xl font-black uppercase tracking-hottr-display">
              How far
              <span className="text-hottr-pink">?</span>
            </h1>
            <p className="mt-2 text-sm text-white/55">You set the bar. We don’t.</p>
            <ul className="mt-6 space-y-2">
              {SCOPES.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => setScope(s.id)}
                    className={cn(
                      'w-full rounded-sm border px-4 py-3 text-left text-[13px] font-semibold uppercase tracking-hottr-label',
                      scope === s.id
                        ? 'border-hottr-pink bg-hottr-pink/10 text-white'
                        : 'border-white/12 text-white/72',
                    )}
                  >
                    {s.label}
                  </button>
                </li>
              ))}
            </ul>
            <label className="mt-6 block text-[10px] font-extrabold uppercase tracking-hottr-label text-white/48">
              Goal in one line
              <input
                value={goalStatement}
                onChange={(e) => setGoalStatement(e.target.value)}
                className="mt-2 w-full rounded-sm border border-white/12 bg-hottr-charcoal px-3 py-3 text-[14px] text-white outline-none focus:border-hottr-pink"
              />
            </label>
            <label className="mt-4 block text-[10px] font-extrabold uppercase tracking-hottr-label text-white/48">
              Desired change
              <input
                value={desiredChange}
                onChange={(e) => setDesiredChange(e.target.value)}
                className="mt-2 w-full rounded-sm border border-white/12 bg-hottr-charcoal px-3 py-3 text-[14px] text-white outline-none focus:border-hottr-pink"
              />
            </label>
            <div className="mt-8 flex gap-2">
              <Button variant="secondary" fullWidth onClick={() => setStep(2)}>
                Back
              </Button>
              <Button variant="primary" fullWidth onClick={() => setStep(4)}>
                Continue
              </Button>
            </div>
          </section>
        )}

        {step === 4 && (
          <section className="mt-3">
            <h1 className="font-display text-3xl font-black uppercase tracking-hottr-display">
              How should we help
              <span className="text-hottr-pink">?</span>
            </h1>
            <ul className="mt-6 space-y-2">
              {STYLES.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => setHelpStyle(s.id)}
                    className={cn(
                      'w-full rounded-sm border px-4 py-4 text-left',
                      helpStyle === s.id
                        ? 'border-hottr-pink bg-hottr-pink/10'
                        : 'border-white/12 bg-white/[0.03]',
                    )}
                  >
                    <span className="block text-[12px] font-extrabold uppercase tracking-hottr-label">
                      {s.label}
                    </span>
                    <span className="mt-1 block text-[12px] text-white/55">{s.blurb}</span>
                  </button>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex gap-2">
              <Button variant="secondary" fullWidth onClick={() => setStep(3)}>
                Back
              </Button>
              <Button variant="primary" fullWidth onClick={() => setStep(5)}>
                Continue
              </Button>
            </div>
          </section>
        )}

        {step === 5 && (
          <section className="mt-3">
            <h1 className="font-display text-3xl font-black uppercase tracking-hottr-display">
              First Breakout
              <span className="text-hottr-pink">.</span>
            </h1>
            <p className="mt-2 text-sm text-white/55">
              V1 proves the engine with <strong className="text-white">Apps → Get Off the Grid</strong>.
            </p>
            <div className="mt-6 rounded-sm border border-hottr-pink/30 bg-hottr-pink/5 p-5">
              <p className="text-[10px] font-extrabold uppercase tracking-hottr-stamp text-hottr-pink">
                7-day Breakout
              </p>
              <h2 className="mt-2 font-display text-2xl font-black uppercase tracking-hottr-display">
                Get Off the Grid
              </h2>
              <p className="mt-2 text-sm text-white/72">
                Interrupt the open-scroll-close loop. Reclaim the hours.
              </p>
              <ul className="mt-4 space-y-1 text-[12px] text-white/55">
                <li className="flex items-center gap-2">
                  <Icon name="check" className="text-hottr-pink text-[16px]" /> Daily Move
                </li>
                <li className="flex items-center gap-2">
                  <Icon name="bolt" className="text-hottr-pink text-[16px]" /> I&apos;m Close when the urge hits
                </li>
                <li className="flex items-center gap-2">
                  <Icon name="trending_up" className="text-hottr-pink text-[16px]" /> Momentum without punishment
                </li>
              </ul>
            </div>
            {error && (
              <p className="mt-4 text-[13px] text-hottr-pink" role="alert">
                {error}
              </p>
            )}
            <div className="mt-8 flex gap-2">
              <Button variant="secondary" fullWidth onClick={() => setStep(4)}>
                Back
              </Button>
              <Button variant="primary" fullWidth onClick={finish}>
                Enrol &amp; start
              </Button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
